declare const RUNNING_ENV: "local" | "preview" | "development" | "staging" | "production" | "test";

export interface StorageLocation {
  bucket: string;
  key: string;
}

export interface SignedURLResult {
  bucket: string;
  key: string;
  url: string;
  expiresAt: string;
}

interface SignedURLsRequest {
  locations: StorageLocation[];
  ttl?: string;
}

interface SignedURLsResponse {
  urls: SignedURLResult[];
}

const getBaseURL = (): string => {
  if (RUNNING_ENV === "local") {
    return "http://localhost:3443";
  }
  if (RUNNING_ENV === "development") {
    return "https://api.storytell-development.ai";
  }
  if (RUNNING_ENV === "preview") {
    throw new Error("Preview environments are not yet implemented");
  }
  if (RUNNING_ENV === "staging") {
    return "https://api.storytell-staging.ai";
  }
  return "https://api.storytell.ai";
};

export class ImagesClient {
  private baseURL: string;

  constructor() {
    this.baseURL = getBaseURL();
  }

  async getSignedURLs(locations: StorageLocation[], ttl?: string, authToken?: string): Promise<SignedURLResult[]> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (authToken) {
      headers["Authorization"] = `Bearer ${authToken}`;
    }

    const requestBody: SignedURLsRequest = {
      locations,
      ...(ttl && { ttl }),
    };

    const response = await fetch(`${this.baseURL}/content/images/signed-urls`, {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get signed URLs: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = (await response.json()) as SignedURLsResponse;
    return data.urls;
  }

  async getSignedURL(location: StorageLocation, ttl?: string, authToken?: string): Promise<string> {
    const results = await this.getSignedURLs([location], ttl, authToken);
    if (results.length === 0) {
      throw new Error("No signed URL returned from server");
    }
    const firstResult = results[0];
    if (!firstResult) {
      throw new Error("No signed URL returned from server");
    }
    return firstResult.url;
  }
}

export const createImagesClient = (): ImagesClient => {
  return new ImagesClient();
};
